import { useQuery } from "@tanstack/react-query";
import {
  notificationCellsApi,
  notificationSettingsQueryKeys,
  type NotificationCell,
  type NotificationEventRow,
  type NotificationSettings,
} from "../../shared/api/notificationSettings";
import { SWITCH } from "./EventChannelTemplates";

const CHANNEL_LABEL: Record<string, string> = { ZBS: "Zalo ZBS", SMS: "SMS", EMAIL: "Email" };

/**
 * The channels a message of this scope actually goes out on (#19594), by the dispatcher's own
 * gates: switched on for the clinic, and switched on in the scope's cell — a service with no row
 * of its own for a channel follows the clinic-wide row (`channelsFor`), and no row at all means on.
 * In the event's order, which decides nothing but the order the messages are created in.
 */
export function sendingChannels(
  event: NotificationEventRow,
  cells: NotificationCell[],
  settings: NotificationSettings | null,
  serviceId: string | null,
): string[] {
  return event.channelOrder.filter((ch) => {
    if (!settings || !settings[SWITCH[ch]]) return false;
    const own = serviceId ? cells.find((c) => c.serviceId === serviceId && c.channel === ch) : undefined;
    const base = cells.find((c) => c.serviceId == null && c.channel === ch);
    return (own ?? base)?.enabled ?? true;
  });
}

/** "Zalo ZBS" · "Zalo ZBS và SMS" · "Zalo ZBS, SMS và Email". */
function joinVi(labels: string[]): string {
  return labels.length <= 1 ? (labels[0] ?? "") : `${labels.slice(0, -1).join(", ")} và ${labels.at(-1)}`;
}

/**
 * The "how it is sent" line (#19594). It used to read "Thứ tự kênh: Zalo ZBS → SMS → Email": an
 * arrow promises a fallback that has not existed since #18935 — every channel switched on is sent —
 * and it listed channels switched off for the whole clinic.
 */
export function sendingChannelsText(channels: string[]): string {
  const labels = channels.map((c) => CHANNEL_LABEL[c] ?? c);
  if (labels.length === 0) return "chưa có kênh nào đang bật — sẽ không gửi";
  if (labels.length === 1) return `qua ${labels[0]}`;
  return `qua ${joinVi(labels)} — gửi trên cả ${labels.length} kênh cùng lúc, không phải kênh dự phòng`;
}

export function SendingChannels({
  event,
  settings,
  serviceId,
}: {
  event: NotificationEventRow;
  settings: NotificationSettings | null;
  serviceId: string | null;
}) {
  // The same query as the cards below, so the line and the cards cannot disagree.
  const cellsQ = useQuery({
    queryKey: notificationSettingsQueryKeys.cells(event.eventKey),
    queryFn: () => notificationCellsApi.list(event.eventKey),
  });
  if (!cellsQ.data) return null;
  return <span data-testid="sending-channels">{sendingChannelsText(sendingChannels(event, cellsQ.data, settings, serviceId))}</span>;
}
